// Pagina pubblica /privacy/access — esercita diritto accesso (Sprint 7).

import { useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { AudiqWordmark, RuleBicolor } from '@audiq/ui';
import { LangSwitcher } from '../components/LangSwitcher';

type State = 'idle' | 'submitting' | 'success' | 'not_found' | 'rate_limited' | 'error';

export function PrivacyAccessPage() {
  const { t } = useTranslation();
  const [identifier, setIdentifier] = useState('');
  const [state, setState] = useState<State>('idle');
  const [errorText, setErrorText] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!identifier.trim()) return;
    setState('submitting');
    setErrorText(null);

    // Autodetect: pickup_code è 7 char con dash (es. "7K-MX9P"), pseudonym è alfanumerico più lungo
    const trimmed = identifier.trim();
    const isPickupCode = /^[A-Z0-9]{2,4}-[A-Z0-9]{2,6}$/i.test(trimmed) || trimmed.length <= 8;
    const body = isPickupCode ? { pickup_code: trimmed } : { pseudonym: trimmed };

    try {
      const res = await fetch('/api/privacy/access-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.status === 404) {
        setState('not_found');
        return;
      }
      if (res.status === 429) {
        setState('rate_limited');
        return;
      }
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string };
        setState('error');
        setErrorText(j.error ?? 'Errore sconosciuto');
        return;
      }
      // Stream zip → trigger download
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audiq-i-tuoi-dati-${Date.now()}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setState('success');
    } catch (err) {
      setState('error');
      setErrorText(err instanceof Error ? err.message : 'Errore di rete');
    }
  }

  return (
    <div className="min-h-dvh" style={{ background: 'var(--surface)', color: 'var(--ink)' }}>
      <header className="flex items-center justify-between p-4">
        <Link to="/privacy">
          <AudiqWordmark size="sm" />
        </Link>
        <LangSwitcher />
      </header>

      <main className="mx-auto max-w-xl p-6">
        <div className="mono mb-2" style={{ color: 'var(--mute)' }}>
          {t('privacy.access.kicker', 'Accesso ai dati')}
        </div>
        <h1 className="serif text-3xl">{t('privacy.access.title', 'Accedi ai tuoi dati')}</h1>
        <RuleBicolor className="my-5 max-w-[10rem]" />
        <p className="mb-6 text-base leading-relaxed" style={{ color: 'var(--mute)' }}>
          {t(
            'privacy.access.intro',
            'Inserisci il tuo pseudonym o il codice di pickup ricevuto a fine conversazione. Audiq genera un file zip con tutto ciò che ha su di te.',
          )}
        </p>

        {state === 'success' ? (
          <div
            className="rounded-md border p-4"
            style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
          >
            <p style={{ color: 'var(--ink)' }}>
              {t(
                'privacy.access.successText',
                'I tuoi dati sono stati scaricati. Conservali in un posto sicuro.',
              )}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="text"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder={t('privacy.access.placeholder', 'pseudonym o codice di pickup') ?? ''}
              className="w-full rounded-md border px-3 py-3"
              style={{
                background: 'var(--paper)',
                borderColor: 'var(--line)',
                color: 'var(--ink)',
              }}
            />
            <button
              type="submit"
              disabled={state === 'submitting'}
              className="rounded-md px-5 py-3 text-sm font-medium"
              style={{
                background: 'var(--primary)',
                color: '#fff',
                opacity: state === 'submitting' ? 0.6 : 1,
              }}
            >
              {state === 'submitting'
                ? t('privacy.access.loading', 'Generazione zip…')
                : t('privacy.access.submit', 'Scarica i miei dati')}
            </button>
            {state === 'not_found' && (
              <p className="text-sm" style={{ color: 'var(--danger)' }}>
                {t('privacy.access.notFound', 'Nessun dato trovato per questo identificativo.')}
              </p>
            )}
            {state === 'rate_limited' && (
              <p className="text-sm" style={{ color: 'var(--danger)' }}>
                {t('privacy.access.rateLimited', 'Troppe richieste, riprova fra 24 ore.')}
              </p>
            )}
            {state === 'error' && errorText && (
              <p className="text-sm" style={{ color: 'var(--danger)' }}>
                {errorText}
              </p>
            )}
          </form>
        )}

        <div className="mt-10 text-sm" style={{ color: 'var(--mute)' }}>
          <Link to="/privacy" className="underline">
            ← {t('privacy.back', 'Torna ai diritti')}
          </Link>
        </div>
      </main>
    </div>
  );
}
